import { ClientBookingData } from "@/redux/apis/clientBooking/types";
import { PointsOfInterest } from "@/redux/apis/clientPoi/types";
import { GuestQuoteRequestInputs, GuestQuoteStopInput } from "@/redux/apis/guest/types";
import { ClientBookingExtra, ClientBookingState } from "@/redux/slices/clientBooking/types";

/**
 * Rehydrate the booking draft from a previously-computed guest quote (widget handoff).
 *
 * An external operator widget deep-links into the flow with only `?widget=v1&quote_uuid={id}` (see the
 * widgets repo's `generateBookUrl`), so GuestBooking refetches the quote (GET /v2/guest-quote/{uuid}) and
 * calls this to turn its whitelisted `request` inputs into a `Partial<ClientBookingState>` — dispatched
 * via `restoreBookingDraft` so the guest lands on a ready-to-book trip.
 *
 * The quote echoes the coordinates the guest already resolved, so pickup/dropoff/stops rehydrate with NO
 * client-side geocoding. The vehicle is stored as a UUID and mapped back to the numeric `vehicle_id` via
 * the guest job-data; the transfer type / extras are enriched from job-data when present but still seed
 * from the quote alone (so a stale reference id doesn't drop the whole selection). Returns `null` when
 * there's nothing usable (no inputs / no locations) so the caller can fall through to a blank draft.
 */
export const hydrateDraftFromGuestQuote = (
  inputs: GuestQuoteRequestInputs | undefined,
  bookingData: ClientBookingData | undefined
): Partial<ClientBookingState> | null => {
  if (!inputs) return null;

  // Every waypoint needs coordinates to seed the draft. Quotes are computed from resolved coords, so all
  // stops should have them; if ANY is missing we bail (return null) rather than dropping it — dropping a
  // stop reindexes the array and could silently promote a dropoff to the pickup. A null draft just means
  // the guest re-enters the trip deliberately.
  const stops = inputs.stops ?? [];
  if (stops.length === 0 || stops.some((stop) => stop.lat == null || stop.lng == null)) return null;
  const points = stops.map((stop) => stopToPoi(stop as GuestQuoteStopInput & { lat: number; lng: number }));

  const draft: Partial<ClientBookingState> = {};

  // Ordered [pickup, ...stops, dropoff]. A lone location seeds pickup only.
  draft.pickup = points[0];
  if (points.length >= 2) {
    draft.destination = points[points.length - 1];
    draft.stops = points.slice(1, -1);
  }

  if (inputs.pickup_datetime) {
    // The quote stores naive local "YYYY-MM-DD HH:mm:ss"; the draft holds an ISO string (the payload
    // builder renders it back to wall-clock). Parse as local (T-separated) so the round-trip preserves it.
    const parsed = new Date(inputs.pickup_datetime.replace(" ", "T"));
    if (!Number.isNaN(parsed.getTime())) draft.pickupDatetime = parsed.toISOString();
  }

  // Vehicle: the quote carries its UUID; the draft keys on the numeric vehicle_id. Map via job-data.
  const vehicle = inputs.vehicle_type ? bookingData?.vehicles.find((v) => v.uuid === inputs.vehicle_type) : undefined;
  if (vehicle) {
    draft.vehicle = {
      vehicle_id: vehicle.vehicle_id,
      uuid: vehicle.uuid,
      name: vehicle.name,
      max_passengers: vehicle.max_passengers,
      max_bags: vehicle.max_bags,
    };
  }

  // Transfer type: enrich from job-data when the numeric id still matches; else seed from the quote alone
  // (bare id + friendly name) so the create still has its required `listed_events_list`.
  if (inputs.transfer_type_id != null) {
    const transfer = bookingData?.transfer_types.find((t) => Number(t.id) === inputs.transfer_type_id);
    draft.transferType = transfer
      ? {
          id: Number(transfer.id),
          label: transfer.label,
          type: transfer.type,
          special_event: transfer.special_event,
          vehicle_exclusions: transfer.vehicle_exclusions,
        }
      : { id: inputs.transfer_type_id, label: inputs.transfer_type_name || undefined };
  }

  // Extras: numeric ids → draft extras, enriched from job-data (name/amount) when present.
  if (inputs.extras?.length) {
    draft.extras = inputs.extras.map((id): ClientBookingExtra => {
      const extra = bookingData?.extras.find((e) => String(e.extra_id) === String(id));
      return extra ? { extra_id: extra.extra_id, name: extra.name, amount: extra.amount } : { extra_id: String(id) };
    });
  }

  if (inputs.job_rate) draft.jobRate = inputs.job_rate;
  if (inputs.booked_for_duration) draft.bookedForDuration = inputs.booked_for_duration;

  return draft;
};

/**
 * Build a minimal PointsOfInterest from a quote stop. Only the fields the booking actually reads
 * (address + coords + city/state/country) carry data; the favourite/vendor metadata is left blank — a
 * deep-linked location isn't a saved POI.
 */
const stopToPoi = (stop: GuestQuoteStopInput & { lat: number; lng: number }): PointsOfInterest => ({
  uuid: "",
  vendor: "",
  vendor_uuid: "",
  vendor_address: "",
  address: stop.address,
  lat: stop.lat,
  lng: stop.lng,
  city: stop.city,
  postcode: "",
  state: stop.state,
  state_code: "",
  country: stop.country,
  country_code: "",
  rm_label: "",
  rm_type: "",
  rm_tags: [],
  rm_exclusions: [],
});
